<template>
    <div
        class="flex h-auto w-full flex-col gap-4 rounded-md border border-gray-200 p-8 shadow-sm"
    >
        <div class="flex h-auto w-full items-center justify-between">
            <p class="text-base font-bold text-gray-800">
                {{
                    $t('campaign_marketing_financial_table_number', {
                        number: index + 1,
                    })
                }}
            </p>
            <div class="flex items-center gap-2">
                <button
                    class="bg-danger-500 hover:bg-danger-600 h-auto w-auto rounded-md px-3 py-2 text-sm font-medium text-white"
                    @click="emit('remove')"
                >
                    <fa icon="fa-trash-can" />
                </button>
                <button
                    class="h-auto w-auto rounded-md bg-gray-200 px-3 py-2 text-sm font-medium"
                    @click="isOpen = !isOpen"
                >
                    <fa
                        icon="fa-chevron-up"
                        :class="{ 'rotate-180': !isOpen }"
                    />
                </button>
            </div>
        </div>

        <div
            v-show="isOpen"
            class="flex h-auto w-full flex-col gap-4"
        >
            <TextField
                v-model="table.title"
                name="campaign_marketing_financial_table_title"
                label="campaign_marketing_financial_table_title"
                help="help_campaign_marketing_financial_table_title"
                class="w-full"
            />

            <div class="w-full overflow-x-auto">
                <div class="flex w-fit min-w-full flex-col">
                    <div
                        class="grid items-center gap-2 border-b border-gray-200 pb-2"
                        :style="{ gridTemplateColumns }"
                    >
                        <div></div>
                        <p class="text-sm font-semibold">
                            {{ $t('campaign_marketing_financial_item') }}
                        </p>
                        <div
                            v-for="(period, periodIndex) in table.periods"
                            :key="periodIndex"
                            class="flex items-center gap-1"
                        >
                            <input
                                :value="period"
                                type="text"
                                class="h-9 min-w-0 flex-1 rounded border border-gray-300 px-2 text-sm font-semibold focus:border-blue-500 focus:outline-none focus:ring-0"
                                @input="
                                    renamePeriod(
                                        periodIndex,
                                        ($event.target as HTMLInputElement)
                                            .value
                                    )
                                "
                            />
                            <button
                                class="h-9 w-auto shrink-0 rounded-md bg-gray-200 px-2 text-xs disabled:opacity-40"
                                :disabled="periodIndex === 0"
                                @click="movePeriod(periodIndex, -1)"
                            >
                                <fa icon="fa-chevron-up" />
                            </button>
                            <button
                                class="h-9 w-auto shrink-0 rounded-md bg-gray-200 px-2 text-xs disabled:opacity-40"
                                :disabled="
                                    periodIndex === table.periods.length - 1
                                "
                                @click="movePeriod(periodIndex, 1)"
                            >
                                <fa icon="fa-chevron-down" />
                            </button>
                            <button
                                class="bg-danger-500 hover:bg-danger-600 h-9 w-10 shrink-0 rounded-md px-2 text-xs text-white"
                                @click="deletePeriod(periodIndex)"
                            >
                                <fa icon="fa-trash-can" />
                            </button>
                        </div>
                        <div></div>
                    </div>

                    <div
                        v-for="(row, rowIndex) in table.rows"
                        :key="row.id"
                        class="grid items-center gap-2 border-t border-gray-200 py-2"
                        :style="{ gridTemplateColumns }"
                    >
                        <div class="flex items-center gap-1">
                            <button
                                class="h-9 w-auto rounded-md bg-gray-200 px-2 text-xs disabled:opacity-40"
                                :disabled="rowIndex === 0"
                                @click="moveRow(rowIndex, -1)"
                            >
                                <fa icon="fa-chevron-up" />
                            </button>
                            <button
                                class="h-9 w-auto rounded-md bg-gray-200 px-2 text-xs disabled:opacity-40"
                                :disabled="rowIndex === table.rows.length - 1"
                                @click="moveRow(rowIndex, 1)"
                            >
                                <fa icon="fa-chevron-down" />
                            </button>
                        </div>
                        <input
                            v-model="row.label"
                            type="text"
                            class="h-9 w-full rounded border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-0"
                        />
                        <input
                            v-for="period in table.periods"
                            :key="period"
                            v-model="row.values[period]"
                            type="text"
                            inputmode="decimal"
                            class="h-9 w-full rounded border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-0"
                        />
                        <button
                            class="bg-danger-500 hover:bg-danger-600 h-9 w-10 rounded-md px-2 text-xs text-white"
                            @click="deleteRow(rowIndex)"
                        >
                            <fa icon="fa-trash-can" />
                        </button>
                    </div>
                </div>
            </div>

            <div class="flex h-auto w-full items-center gap-2">
                <button
                    class="h-auto w-auto rounded-md border bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
                    @click="addRow"
                >
                    {{ $t('campaign_marketing_financial_add_row') }}
                </button>
                <button
                    class="h-auto w-auto rounded-md border bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
                    @click="addPeriod"
                >
                    {{ $t('campaign_marketing_financial_add_period') }}
                </button>
            </div>

            <TextField
                v-model="table.note"
                name="campaign_marketing_financial_table_note"
                label="campaign_marketing_financial_table_note"
                help="help_campaign_marketing_financial_table_note"
                class="w-full"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import TextField from '@src/components/TextField.vue'
import { FinancialTable } from '@src/types'
import { generateUuid } from '@src/utils/helpers'
import { useVModel } from '@vueuse/core'
import { computed, ref } from 'vue'

const props = defineProps<{
    modelValue: FinancialTable
    namePrefix: string
    index: number
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: FinancialTable): void
    (e: 'remove'): void
}>()

const table = useVModel(props, 'modelValue', emit)

const isOpen = ref(true)

const gridTemplateColumns = computed(() => {
    // repeat() requires a positive integer; repeat(0, ...) is invalid CSS and
    // would drop the whole declaration, so omit it entirely when there are no periods
    const columns = ['4rem', '10rem']
    if (table.value.periods.length > 0) {
        columns.push(`repeat(${table.value.periods.length}, 16rem)`)
    }
    columns.push('3rem')
    return columns.join(' ')
})

const renamePeriod = (index: number, newLabel: string) => {
    const oldLabel = table.value.periods[index]
    if (oldLabel === newLabel) return

    table.value.periods[index] = newLabel
    table.value.rows.forEach((row) => {
        const existingValue =
            oldLabel in row.values ? row.values[oldLabel] : null
        delete row.values[oldLabel]
        row.values[newLabel] = existingValue
    })
}

const addPeriod = () => {
    const label = `Period ${table.value.periods.length + 1}`
    table.value.periods.push(label)
    table.value.rows.forEach((row) => {
        row.values[label] = null
    })
}

const deletePeriod = (index: number) => {
    const label = table.value.periods[index]
    table.value.periods.splice(index, 1)
    table.value.rows.forEach((row) => {
        delete row.values[label]
    })
}

const movePeriod = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= table.value.periods.length) return
    const periods = table.value.periods
    ;[periods[index], periods[target]] = [periods[target], periods[index]]
}

const addRow = () => {
    const values: Record<string, string | null> = {}
    table.value.periods.forEach((period) => {
        values[period] = null
    })
    table.value.rows.push({
        id: generateUuid(),
        label: '',
        values,
    })
}

const deleteRow = (index: number) => {
    table.value.rows.splice(index, 1)
}

const moveRow = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= table.value.rows.length) return
    const rows = table.value.rows
    ;[rows[index], rows[target]] = [rows[target], rows[index]]
}
</script>
